import enums from '@/helpers/enums';

// Shared lookups for the marketing area (campaigns + audiences). These were copy-pasted into every screen
// that renders a status chip; one copy so a new status or platform only has to be added in enums/index.js.

const UNKNOWN = { label: '—', color: 'grey' };
const UNDER_REVIEW = { label: 'Under Review', color: 'warning' };

/**
 * Under Review is derived (see planItemStatus), not one of AUDIENCE.ITEM_STATUS, so it has no id for a status
 * picker to filter on. Negative so it can never collide with a real status id.
 */
export const PLAN_ITEM_UNDER_REVIEW = -1;

/** Campaign-level status chip (see MARKETING.CAMPAIGN_STATUS). */
export function campaignStatus(status) {
  return enums.MARKETING.CAMPAIGN_STATUS[status] || UNKNOWN;
}

/** Options for a campaign status picker, in status order (see MARKETING.CAMPAIGN_STATUS). */
export function campaignStatusOptions() {
  return Object.keys(enums.MARKETING.CAMPAIGN_STATUS).map((id) => ({
    value: Number(id),
    text: enums.MARKETING.CAMPAIGN_STATUS[id].label,
    color: enums.MARKETING.CAMPAIGN_STATUS[id].color
  }));
}

/**
 * What the campaign list opens on: drafts — the only campaigns still waiting on a person to approve them. Scheduled
 * is already handled and Sending/Sent/Failed are history; both would bury that working set once a few months of
 * sends pile up, so they're one click away instead of on screen by default. A fresh array each call because the
 * picker mutates whatever it's given.
 */
export function defaultCampaignStatusFilter() {
  const ids = enums.MARKETING.CAMPAIGN_STATUS_IDS;
  return [ids.DRAFT];
}

/** True when a campaign passes a status picker's selection; nothing selected means "all statuses". */
export function matchesStatus(campaign, statuses) {
  if (!statuses || !statuses.length) return true;
  if (!campaign) return false;
  return statuses.indexOf(campaign.status) !== -1;
}

/** Per-item status chip — an email or one network's post (see MARKETING.ITEM_STATUS). */
export function itemStatus(status) {
  return enums.MARKETING.ITEM_STATUS[status] || UNKNOWN;
}

/** Display name for a social network. */
export function platformName(platform) {
  return enums.MARKETING.PLATFORM[platform] || '';
}

/** Display name for a campaign objective. */
export function objectiveName(objective) {
  return enums.MARKETING.OBJECTIVE[objective] || '';
}

/**
 * What picking an objective actually does, in the author's terms. The objective isn't just a label: it goes into
 * the generation prompt (CampaignService writes "Objective: X" into the brief), and the four conversion
 * objectives — Traffic, Promotion, Seasonal, ProductLaunch — also change where the email's links point, sending
 * readers to the "start designing" Maker page instead of the informational landing page (DefaultTargetUrl).
 */
export function objectiveHint(objective) {
  return enums.MARKETING.OBJECTIVE_HINT[objective] || '';
}

/** Display name for a target/audience type (Category, Industry, Topic, DesignGroup, ContentPage). */
export function targetTypeName(targetType) {
  return enums.MARKETING.TARGET_TYPE[targetType] || '';
}

/**
 * True when an email for this campaign reaches the ENTIRE list rather than a slice of it — no Customer.io work_area
 * filter, so the broadcast falls back to its own audience. The server resolves this (Campaign.EveryoneEmail, from
 * CampaignService.GetWorkAreaValues) because the rule isn't just "has an Industry/Topic target": a Purpose or
 * Platform industry is nobody's work area, so targeting one is also a send to everyone. Social campaigns are never
 * "to everyone" in this sense.
 */
export function isEveryoneEmail(campaign) {
  if (!campaign || campaign.type !== enums.MARKETING.CAMPAIGN_TYPE.EMAIL) return false;
  return !!campaign.everyoneEmail;
}

/** The key a campaign's audiences are addressed by — mirrors CampaignService.AudienceKey on the server. */
export function audienceKey(targetType, targetId) {
  return `${targetType}:${targetId}`;
}

/**
 * True when a campaign belongs on the calendar/list for the given audience key (from audienceKey(); falsy means
 * "no audience filter", so everything passes). A campaign matches when it targets that audience — an Industry
 * campaign matches every Topic beneath it, expanded server-side into Campaign.AudienceKeys — OR when it isn't
 * aimed at anyone in particular (Campaign.ReachesEveryone): an email broadcast to the whole list, or a social post
 * with no Industry/Topic subject. Those genuinely land in front of this audience, so hiding them would make the
 * filtered calendar look emptier than the audience's week actually is.
 */
export function matchesAudience(campaign, key) {
  if (!key) return true;
  if (!campaign) return false;
  if (campaign.reachesEveryone) return true;
  return (campaign.audienceKeys || []).indexOf(key) !== -1;
}

/**
 * Network-reach picker values — how far a social campaign's fan-out actually goes.
 *
 * Pinterest is the network every social campaign gets by default, so "which posts go anywhere else" is the
 * question worth asking of a list or a calendar; a checkbox per network would answer "Pinterest: yes" over and
 * over. ALL means every ENABLED network (see Marketing settings), not every network in the enum — a network
 * that's switched off can't be posted to, so no campaign could ever match if it counted.
 */
export const NETWORK_REACH = { PINTEREST_ONLY: 1, MULTIPLE: 2, ALL: 3 };

/** Options for the network-reach picker, widest reach first. */
export function networkReachOptions() {
  return [
    { value: NETWORK_REACH.ALL, text: 'All networks' },
    { value: NETWORK_REACH.MULTIPLE, text: 'More than one network' },
    { value: NETWORK_REACH.PINTEREST_ONLY, text: 'Pinterest only' }
  ];
}

/**
 * True when a campaign passes the network-reach picker; nothing selected means "any reach".
 *
 * An email campaign has no networks at all, so it fails EVERY selection rather than passing them — picking a
 * reach is asking about social posts, and letting emails through would make the answer unreadable.
 *
 * `enabledPlatforms` is Marketing settings' enabledPlatforms (see MARKETING.PLATFORM ids). When it hasn't
 * loaded yet, ALL falls back to MULTIPLE rather than matching nothing: an empty enabled list would otherwise
 * make every campaign vacuously "all networks", which is the wrong way to be wrong.
 */
export function matchesNetworkReach(campaign, reach, enabledPlatforms) {
  if (!reach) return true;
  if (!campaign) return false;

  const platforms = campaign.selectedPlatforms || [];
  if (!platforms.length) return false;

  const ids = enums.MARKETING.PLATFORM_IDS;
  if (reach === NETWORK_REACH.PINTEREST_ONLY) return platforms.every((p) => p === ids.PINTEREST);
  if (reach === NETWORK_REACH.MULTIPLE) return platforms.length > 1;

  // Google Display belongs to Ads, which never reaches this list, so it can't be part of "all networks".
  const enabled = (enabledPlatforms || []).filter((p) => p !== ids.GOOGLE_DISPLAY);
  if (!enabled.length) return platforms.length > 1;
  return enabled.every((p) => platforms.indexOf(p) !== -1);
}

/**
 * Status chip for one audience plan item (see AUDIENCE.ITEM_STATUS), including the states that aren't
 * persisted on the item because the real work moved somewhere else:
 *
 * - Written-but-unpublished. The decision is made and the content exists, so what's left is a human review —
 *   "Approved" would hide that the page is sitting there waiting, and "Done" would claim it's live when it
 *   isn't. Published items come back as Done from the server, because a live page IS the finished work.
 * - Realized into a campaign. Approving the item only creates a DRAFT campaign; someone still has to approve
 *   that campaign for it to go out. So the chip tracks the campaign from there on: Under Review until the
 *   campaign is approved, Approved once it's scheduled, then Sending/Failed. A sent campaign comes back as
 *   Done from the server for the same reason a published page does.
 */
export function planItemStatus(item) {
  if (!item) return UNKNOWN;
  if (item.contentUrl && !item.contentPublished) return UNDER_REVIEW;

  if (item.campaignId && item.campaignStatus) {
    const ids = enums.MARKETING.CAMPAIGN_STATUS_IDS;
    if (item.campaignStatus === ids.DRAFT) return UNDER_REVIEW;
    if (item.campaignStatus === ids.SCHEDULED) return { label: 'Approved', color: 'primary' };
    // Sent falls through to the item's own status (Done) — everything else reads off the campaign.
    if (item.campaignStatus !== ids.SENT) return campaignStatus(item.campaignStatus);
  }

  return enums.AUDIENCE.ITEM_STATUS[item.status] || UNKNOWN;
}

/**
 * What a plan item filters as: its own status, except when the chip says Under Review — those come back as
 * PLAN_ITEM_UNDER_REVIEW instead of the status underneath (Approved, or Suggested for written-but-unpublished
 * content), so the picker matches what the card shows rather than what the row stores.
 */
export function planItemStatusKey(item) {
  if (!item) return 0;
  return planItemStatus(item) === UNDER_REVIEW ? PLAN_ITEM_UNDER_REVIEW : item.status;
}

/**
 * A plan's planned week as the marketer reads it — "Oct 5". Plan dates are real Mondays, not offsets, so they
 * mean the same thing on every redraft. The year is added only when it isn't this one.
 */
export function weekOf(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const options = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
  return d.toLocaleDateString(undefined, options);
}

/** Severity chip for one audience content gap (see AUDIENCE.GAP_SEVERITY). */
export function gapSeverity(severity) {
  return enums.AUDIENCE.GAP_SEVERITY[severity] || UNKNOWN;
}
